import { Card, CardContent, Typography, Stack, Box, Link } from "@mui/material";
import LinearProgress, {
  linearProgressClasses,
} from "@mui/material/LinearProgress";
import { styled } from "@mui/material/styles";

import { MY_PERF_SCROLL_LIST_PB } from "../../Dashboard/layouts";
import GoalsDataDisclaimer from "../GoalsDataDisclaimer";

// Helper: pick bar color
const getBarColor = (category = "") => {
  const value = category.toLowerCase();

  if (value === "red") return "#F57272";
  if (value === "yellow") return "#FEBD78";
  if (value === "green") return "#54C467";

  return "#54C467";
};

// Reusable progress bar (BorderLinearProgress-like)
const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
  height: 10,
  borderRadius: 6,
  backgroundColor: "#F1F1F1",
  border: "1px solid #E6E6E6",
  overflow: "hidden",
  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 6,
  },
}));

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function PercentMarker({ pct }) {
  const p = Math.max(0, Math.min(100, pct));

  const isStart = p === 0;
  const isEnd = p === 100;

  return (
    <Box sx={{ position: "relative", height: 26, mt: 0.75 }}>
      <Box
        sx={{
          position: "absolute",
          left: isStart ? 0 : isEnd ? "100%" : `${p}%`,
          transform: isStart
            ? "translateX(0)"
            : isEnd
              ? "translateX(-100%)"
              : "translateX(-50%)",
          display: "flex",
          flexDirection: "column",
          alignItems: isStart ? "flex-start" : isEnd ? "flex-end" : "center",
        }}
      >
        <Box
          sx={{
            width: 0,
            height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderBottom: "8px solid #7A7A7A",
            mb: "2px",
            ...(isStart && { marginLeft: 0 }),
            ...(isEnd && { marginRight: 0 }),
          }}
        />

        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: "#444",
            lineHeight: 1,
            textAlign: isStart ? "left" : isEnd ? "right" : "center",
            width: "100%",
          }}
        >
          {p}%
        </Typography>
      </Box>
    </Box>
  );
}

export default function RegionTrackingCard({
  title = "Region Tracking by % to Goal",
  regions = null,
  onExplore,
  noGoalsDataAvailable = false,
  goalsDataDisclaimerShow = false,
}) {
  const displayRegions = Array.isArray(regions) ? regions : [];
  const hasData = displayRegions.length > 0;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "8px",
        border: "0.5px solid #F0F0F0",
        boxShadow: "1px 4px 4px 0px #00000008",
        height: "100%",
      }}
    >
      <CardContent
        sx={{
          p: "24px",
          height: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, mb: 2, fontSize: 14, lineHeight: 1.2 }}
        >
          {title}
        </Typography>

        {goalsDataDisclaimerShow && <GoalsDataDisclaimer />}

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            pr: 1,
            pb: MY_PERF_SCROLL_LIST_PB,
            overflowY: "auto",
            maxHeight: "400px",
            msOverflowStyle: "none",
          }}
        >
          {noGoalsDataAvailable ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
                textAlign: "center",
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  color: "#718096",
                }}
              >
                Stay tuned - New Goals for current Quarter to be available soon
              </Typography>
            </Box>
          ) : hasData ? (
            <Stack spacing={0}>
              {displayRegions.map((r) => {
                const pct = clamp(Number(r.percent) || 0, 0, 100);
                const barColor = getBarColor(r.category);

                return (
                  <Box
                    key={r.id}
                    sx={{
                      borderRadius: "8px",
                      py: 1.5,
                      bgcolor: "#FFFFFF",
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mb: 1 }}
                    >
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {r.name}
                      </Typography>

                      <Link
                        component="button"
                        underline="hover"
                        onClick={() => onExplore?.(r)}
                        sx={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: "#E56800",
                          cursor: "pointer",
                        }}
                      >
                        Explore
                      </Link>
                    </Stack>

                    <Box sx={{ position: "relative" }}>
                      <BorderLinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          [`& .${linearProgressClasses.bar}`]: {
                            backgroundColor: barColor,
                          },
                        }}
                      />
                      <PercentMarker pct={pct} />
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 4,
              }}
            >
              <Typography
                sx={{
                  fontSize: 14,
                  color: "#718096",
                }}
              >
                No data found
              </Typography>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
